import './ScrollToTop.css';
import React, { useEffect, useState, useCallback, useContext, useRef } from 'react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import throttle from 'lodash/throttle';
import { SitecoreContextReactContext } from '../../utils/SitecoreContextReactContext';
import { tealiumClickFunc } from '../../utils/tealium';
import { canUseDOM, get, getGlobalDictionaryTerm } from '../../utils';
import IconArrowRight from '../../components/atoms/Icons/IconArrowRight';
const BUTTON_FOOTER_DISTANCE = 10;
const BUTTON_SIZE = 58;
const FIXED_BOTTOM_OFFSET = 80 + BUTTON_SIZE;
const FIXED_BOTTOM_OFFSET_DESK = 85 + BUTTON_SIZE;
const FIXED_BOTTOM_OFFSET_POI_STICKY_CTA = 110 + BUTTON_SIZE;
const ScrollToTop = _ref => {
  let {
    offset = 100,
    analytics
  } = _ref;
  const sitecoreContext = useContext(SitecoreContextReactContext);
  const rtl = Boolean(sitecoreContext && sitecoreContext.rtl);
  const toTopLabel = getGlobalDictionaryTerm(sitecoreContext, 'toTopLabel') || "To top";
  const [visible, setVisibility] = useState(false);
  const [stickyOffset, setStickyOffset] = useState('100%');
  const footerBottomRef = useRef(null);
  const mutationObserverRef = useRef(null);
  const handleScrollVisibility = useCallback(debounce(() => {
    if (canUseDOM) {
      const html = document.scrollingElement || document.documentElement;
      if (!html) return;
      if (html.scrollTop > offset && !visible) setVisibility(true);
      if (!html.scrollTop && visible) setVisibility(false);
    }
  }, 100, {
    leading: false,
    trailing: true
  }), [visible]);
  useEffect(() => {
    if (typeof window === undefined) return;
    window.addEventListener('scroll', handleScrollVisibility);
    return () => {
      window.removeEventListener('scroll', handleScrollVisibility);
      handleScrollVisibility.cancel();
    };
  }, [handleScrollVisibility]);
  const handleButtonClick = useCallback(() => {
    if (canUseDOM) {
      const html = document.scrollingElement || document.documentElement;
      if (html) html.scrollTop = 0;
    }
  }, []);
  const handleResize = useCallback(throttle(() => {
    if (footerBottomRef.current) {
      const stickyBreakpoint = footerBottomRef.current.getBoundingClientRect().top + window.scrollY - BUTTON_FOOTER_DISTANCE;
      setStickyOffset(`${stickyBreakpoint}px`);
    }
  }, 1000), []);
  const handleMutation = useCallback(mutations => {
    handleResize();
  }, [handleResize]);
  const observerInit = useCallback(() => {
    const mainElement = document.querySelector('.js--main');
    const footerElement = document.querySelector('.js--footer');
    mutationObserverRef.current = new MutationObserver(handleMutation);
    mutationObserverRef.current.observe(mainElement, {
      childList: true,
      subtree: true,
      attributes: true
    });
    mutationObserverRef.current.observe(footerElement, {
      childList: true,
      subtree: true,
      attributes: true
    });
  }, [handleMutation]);
  const handleScrollSticky = useCallback(throttle(() => {
    const footerBottomRefNode = document.querySelector('.js--footer-bottom');
    if (!footerBottomRef.current && footerBottomRefNode) {
      footerBottomRef.current = footerBottomRefNode;
      window.removeEventListener('scroll', handleScrollSticky);
      observerInit();
      handleResize();
    }
  }, 100), []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('scroll', handleScrollSticky);
    handleScrollSticky();
    return () => {
      window.removeEventListener('scroll', handleScrollSticky);
    };
  }, [handleScrollSticky]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      mutationObserverRef.current && mutationObserverRef.current.disconnect();
    };
  }, [handleResize]);
  return /*#__PURE__*/React.createElement(StyledWrapper, {
    rtl: rtl,
    className: clsx('my-icon-white back-to-top', disableInPrint, {
      'back-to-top--visible': visible
    }),
    onClick: () => {
      tealiumClickFunc(get(analytics, 'goToTopAnalytics', {}), 'Scroll Up');
      handleButtonClick();
    },
    textLabel: toTopLabel,
    stickyOffset: stickyOffset
  }, /*#__PURE__*/React.createElement(IconArrowRight, {
    className: "icon back-to-top__icon"
  }));
};
const StyledWrapper = _ref2 => {
  let {
    children,
    rtl,
    className,
    onClick,
    textLabel,
    stickyOffset
  } = _ref2;
  return /*#__PURE__*/React.createElement("span", {
    className: "to-top-container",
    style: {
      '--sticky-bottom-offset': stickyOffset
    }
  }, /*#__PURE__*/React.createElement("button", {
    className: `m-0 ${commonStyles} ${rtl ? rtlStyles : ltrStyles} ${className}`,
    onClick: onClick,
    tabIndex: 0,
    "aria-label": textLabel,
    title: textLabel,
    style: {
      '--button-size': `${BUTTON_SIZE}px`,
      '--fixed-bottom-offset': FIXED_BOTTOM_OFFSET,
      '--fixed-bottom-offset-desk': FIXED_BOTTOM_OFFSET_DESK,
      '--fixed-bottom-offset-with-fixed-poi-cta': FIXED_BOTTOM_OFFSET_POI_STICKY_CTA
    }
  }, children));
};
const disableInPrint = "d8axrx0";
const commonStyles = "c1v58enf";
const ltrStyles = "lytijta";
const rtlStyles = "rtef2i8";
export default ScrollToTop;